import {
    Button,
    Container,
    createStyles, FormControl, InputLabel,
    makeStyles, MenuItem, Select,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TableRow, TextField,
    Theme,
} from '@material-ui/core'
import {Link} from 'react-router-dom'
import React, {useCallback, useEffect, useRef, useState} from 'react'
import Paper from "@material-ui/core/Paper";
import dateFormatting from "../../scripts/dateFormatting";
import actionsWithApi from "../../customHooks/actionsWithApi";
import {URL_FOR_API} from "../../constants/constants";
import {StyledTableCell, StyledTableRow} from "../DraftContractPage/styled";
import s from "../FindUser/FindUser.module.scss";
import SearchIcon from "@material-ui/icons/Search";
import {allStatuses, StatusNameMap} from "../DraftContractPage/types";
import Preloader from "../shared/Preloader/Preloader";
import getCookie from "../../scripts/getCookie";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        pleaseLogin: {
            textAlign: 'center',
            padding: '20px 0',
            display: 'flex',
            justifyContent: 'center'
        },
        container: {
            paddingTop: 50,
        },
        header: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 20,
        },
        table: {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
        },
        viewMoreButton: {
            display: 'inline-block',
            margin: '20px auto',
        },
        formControl: {
            width: 200,
            marginRight: 20,
            marginBottom: 20,
            "&:last-child": {
                marginRight: 0
            },
            [theme.breakpoints.down('xs')]: {
                width: "100%",
                marginRight: 0
            }
        },
        searchField: {
            width: 200,
            [theme.breakpoints.down('xs')]: {
                width: "100%",
                marginRight: 0
            }
        },
        noResults: {
            padding: '20px 0',
            display: 'flex',
            justifyContent: 'center'
        }
    }),
);

export default function DraftContractsListPage() {
    const classes = useStyles();
    const token = getCookie("token")
    const [contracts, setContracts] = useState<any[]>([]);
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(10);
    const searchTimerRef = useRef<NodeJS.Timeout | null>(null);
    const [email, setEmail] = useState('');
    const [status, setStatus] = useState('');
    const [newOnly, setNewOnly] = useState('');
    const [viewMoreBtnIsVisible, setViewMoreBtnIsVisible] = useState<boolean>(true);
    const [viewMoreLoaderIsActive, setViewMoreLoaderIsActive] = useState<boolean>(false);

    const getContracts = useCallback(async (page?: number) => {
        try {
            let url: string = `/api/v1/ContractDraft/all?Pagination.PageSize=${pageSize}&Pagination.PageNumber=${pageNumber}`;
            if (email) {
                url += `&CreatorEmail=${email}`;
            }
            if (status) {
                url += `&Status=${status}`;
            }
            if (newOnly) {
                url += `&NewOnly=${newOnly === 'yes'}`;
            }
            const response = await actionsWithApi("GET", URL_FOR_API + url);
            if (response && response.status === 200 && response.data) {
                if (response.data.length > 0) {
                    if (page) {
                        setContracts([...contracts, ...response.data]);
                    } else {
                        setContracts(response.data);
                    }
                    setViewMoreBtnIsVisible(response.data.length === pageSize);
                    setViewMoreLoaderIsActive(false);
                } else {
                    setContracts([]);
                    setViewMoreBtnIsVisible(false);
                    setViewMoreLoaderIsActive(false);
                }
            }
        } catch (e) {
            console.error('Error fetching contracts:', e);
        }
    }, [newOnly, email, status, pageSize, pageNumber]);

    const handleInput = (pageSize: string) => {
        let size = Number(pageSize);
        setPageSize(size)
    };

    const handleSearchInput = async (searchQuery: string): Promise<void> => {
        if (searchTimerRef.current) {
            clearTimeout(searchTimerRef.current)
        }

        searchTimerRef.current = setTimeout(() => {
            setEmail(searchQuery)
        }, 500)
    }

    useEffect(() => {
        if (pageNumber && token) {
            getContracts(pageNumber);
        }
    }, [pageNumber, token]);

    useEffect(() => {
        if (token) {
            getContracts();
        }
    }, [newOnly, status, token]);

    if (!token) {
        return (
            <h1 className={classes.pleaseLogin}>Please, Login!</h1>
        )
    }
    
    return (
        <Container maxWidth="lg" className={classes.container}>
            <div className={classes.header}>
                <FormControl className={classes.formControl}>
                    <InputLabel id="user-type-label">Status</InputLabel>
                    <Select labelId="user-type-label" name="userType" value={status} onChange={(e) => setStatus(e.target.value + "")} >
                        <MenuItem value="">Select</MenuItem>
                        {
                            allStatuses.map((status: string) => (
                                <MenuItem value={status} key={status}>{StatusNameMap[status]}</MenuItem>
                            ))
                        }
                    </Select>
                </FormControl>

                <FormControl className={classes.formControl}>
                    <InputLabel id="registered-status-label">New Only</InputLabel>
                    <Select labelId="registered-status-label" name="registeredStatus" value={newOnly} onChange={(e) => setNewOnly(e.target.value + "")} >
                        <MenuItem value="">Select</MenuItem>
                        <MenuItem value="yes">Yes</MenuItem>
                        <MenuItem value="no">No</MenuItem>
                    </Select>
                </FormControl>

                <label className={s["FindUser-Search"]}>
                    <SearchIcon className="PrimaryIcon" />
                    <TextField className={classes.searchField} label="Creator Email" type="search" onChange={(e) => handleSearchInput(e.target.value + "")} />
                </label>

                <label className={s["FindUser-Search"]}>
                    <TextField className={classes.searchField} label="Items per page" type="number" value={pageSize} onChange={(e) => handleInput(e.target.value)} />
                </label>
                <Button color="primary" variant="outlined" onClick={() => getContracts()}>Apply</Button>
            </div>
            <div className={classes.table}>
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <StyledTableCell align="left">Creator Email</StyledTableCell>
                                <StyledTableCell align="center">Status</StyledTableCell>
                                <StyledTableCell align="center">Version</StyledTableCell>
                                <StyledTableCell align="center">Date Created</StyledTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {contracts.length > 0 && contracts.map((contract: any) => (
                                <StyledTableRow key={contract.id}>
                                    <StyledTableCell align="left">
                                        <Link
                                            to={{
                                                pathname: `/contract-drafts/${contract.id}`,
                                            }}
                                        >
                                            {contract.creatorEmail || '<NO EMAIL>'}
                                        </Link>
                                    </StyledTableCell>
                                    <StyledTableCell align="center">{StatusNameMap[contract.actualStatus]}</StyledTableCell>
                                    <StyledTableCell align="center">{contract.version}</StyledTableCell>
                                    <StyledTableCell align="center">{dateFormatting(contract.createDate)}</StyledTableCell>
                                </StyledTableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                {
                    contracts.length === 0 && (
                        <span className={classes.noResults}>No contracts</span>
                    )
                }

                {viewMoreBtnIsVisible &&
                    <Button color="primary" variant="outlined" className={classes.viewMoreButton}
                            onClick={() => {
                                setPageNumber(pageNumber + 1)
                                setViewMoreLoaderIsActive(true)
                            }}>
                        View more
                    </Button>
                }

                {viewMoreLoaderIsActive &&
                    <Preloader size="50" position="center" />
                }

            </div>
        </Container>
    )
}
